import { useState, useEffect, useCallback } from 'react';
import {
  getProfile,
  updateGeneralProfile,
  updatePersonalProfile,
  updateCompanyProfile,
  updateBillingProfile,
} from '@/api/users';
import FilesApi from '@/api/files';
import { useToast } from '@/components/shared/ToastProvider';
import { useAuth } from '@/context/AuthContext';

// Типи для секцій
type SectionType = 'general' | 'personal-details' | 'billing' | 'company';

// Інтерфейс для всіх даних профілю
interface ProfileData {
  // Загальні поля
  email: string;
  isEmailConfirmed: string;
  firstname: string;
  lastname: string;
  avatar: string;

  // Персональні дані (з префіксом personal_)
  personal_country: string;
  personal_province: string;
  personal_city: string;
  personal_address: string;
  personal_apt_suite: string;
  personal_unit: string;
  personal_municipality: string;
  personal_postal_code: string;
  personal_phone: string;
  personal_info_agreement: string;
  terms_privacy_agreement: string;
  personal_age_group: string;

  // Банківські дані
  // Для лендлорда
  account_holder_first_name: string;
  account_holder_last_name: string;
  bank_name: string;
  bank_branch_address: string;
  transit_number: string;
  institution_number: string;
  account_number: string;
  account_type: string;
  swift_bic_code: string;
  billing_authorization: string;

  // Для тенанта (додаткові поля з префіксом tenant_pad_)
  tenant_pad_account_holder: string;
  tenant_pad_bank_name: string;
  tenant_pad_bank_branch_address: string;
  tenant_pad_transit_number: string;
  tenant_pad_institution_number: string;
  tenant_pad_account_number: string;
  tenant_pad_account_type: string;
  tenant_pad_swift_bic_code: string;
  tenant_pad_authorization: string;

  // Компанійні дані (з префіксом company_)
  company_name: string;
  company_position: string;
  company_country: string;
  company_province: string;
  company_city: string;
  company_address: string;
  company_apt_suite: string;
  company_unit: string;
  company_municipality: string;
  company_postal_code: string;
  company_phone: string;
  company_email: string;
  company_department: string;
}

// Створення початкового стану
const createInitialProfileData = (profileResponse: any): ProfileData => {
  return {
    // Загальні поля
    email: profileResponse.email || '',
    isEmailConfirmed: String(profileResponse.isEmailConfirmed ?? false),
    firstname: profileResponse.firstname || '',
    lastname: profileResponse.lastname || '',
    avatar: profileResponse.avatar || '',

    // Персональні дані
    personal_country: profileResponse.personal?.personal_country || '',
    personal_province: profileResponse.personal?.personal_province || '',
    personal_city: profileResponse.personal?.personal_city || '',
    personal_address: profileResponse.personal?.personal_address || '',
    personal_apt_suite: profileResponse.personal?.personal_apt_suite || '',
    personal_unit: profileResponse.personal?.personal_unit || '',
    personal_municipality:
      profileResponse.personal?.personal_municipality || '',
    personal_postal_code: profileResponse.personal?.personal_postal_code || '',
    personal_phone: profileResponse.personal?.personal_phone || '',
    personal_info_agreement:
      profileResponse.personal?.personal_info_agreement || 'false',
    terms_privacy_agreement:
      profileResponse.personal?.terms_privacy_agreement || 'false',
    personal_age_group: profileResponse.personal?.personal_age_group || '',

    // Банківські дані для лендлорда
    account_holder_first_name:
      profileResponse.billing?.account_holder_first_name || '',
    account_holder_last_name:
      profileResponse.billing?.account_holder_last_name || '',
    bank_name: profileResponse.billing?.bank_name || '',
    bank_branch_address: profileResponse.billing?.bank_branch_address || '',
    transit_number: profileResponse.billing?.transit_number || '',
    institution_number: profileResponse.billing?.institution_number || '',
    account_number: profileResponse.billing?.account_number || '',
    account_type: profileResponse.billing?.account_type || '',
    swift_bic_code: profileResponse.billing?.swift_bic_code || '',
    billing_authorization:
      profileResponse.billing?.billing_authorization || 'false',

    // Банківські дані для тенанта
    tenant_pad_account_holder:
      profileResponse.billing?.tenant_pad_account_holder || '',
    tenant_pad_bank_name: profileResponse.billing?.tenant_pad_bank_name || '',
    tenant_pad_bank_branch_address:
      profileResponse.billing?.tenant_pad_bank_branch_address || '',
    tenant_pad_transit_number:
      profileResponse.billing?.tenant_pad_transit_number || '',
    tenant_pad_institution_number:
      profileResponse.billing?.tenant_pad_institution_number || '',
    tenant_pad_account_number:
      profileResponse.billing?.tenant_pad_account_number || '',
    tenant_pad_account_type:
      profileResponse.billing?.tenant_pad_account_type || '',
    tenant_pad_swift_bic_code:
      profileResponse.billing?.tenant_pad_swift_bic_code || '',
    tenant_pad_authorization:
      profileResponse.billing?.tenant_pad_authorization || 'false',

    // Компанійні дані
    company_name: profileResponse.company?.company_name || '',
    company_position: profileResponse.company?.company_position || '',
    company_country: profileResponse.company?.company_country || '',
    company_province: profileResponse.company?.company_province || '',
    company_city: profileResponse.company?.company_city || '',
    company_address: profileResponse.company?.company_address || '',
    company_apt_suite: profileResponse.company?.company_apt_suite || '',
    company_unit: profileResponse.company?.company_unit || '',
    company_municipality: profileResponse.company?.company_municipality || '',
    company_postal_code: profileResponse.company?.company_postal_code || '',
    company_phone: profileResponse.company?.company_phone || '',
    company_email: profileResponse.company?.company_email || '',
    company_department: profileResponse.company?.company_department || '',
  };
};

export const useProfile = (sectionType: SectionType) => {
  const { isTenant } = useAuth();
  const { showSuccess, showError } = useToast();

  const [profileData, setProfileData] = useState<ProfileData>(
    {} as ProfileData,
  );
  const [initialData, setInitialData] = useState<ProfileData>(
    {} as ProfileData,
  );
  const [loading, setLoading] = useState(true);

  // Завантаження даних (один раз для всіх секцій)
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const response = await getProfile();
        const initialProfileData = createInitialProfileData(response.data);

        setProfileData(initialProfileData);
        setInitialData(initialProfileData);
      } catch (error) {
        showError('Failed to load profile data.');
        console.error('Failed to load profile data', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [showError]);

  // Загальна функція зміни полів
  const handleChange = (fieldKey: keyof ProfileData, value: string) => {
    setProfileData((prev) => ({ ...prev, [fieldKey]: value }));
  };

  // Функція для завантаження аватара через FilesApi
  /*
  const uploadAvatar = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async () => {
        try {
          const base64Content = reader.result as string;

          const response = await FilesApi.uploadAvatar({
            content: base64Content,
            originalName: file.name,
          });

          resolve(response.data.url);
        } catch (error) {
          console.error('Error uploading avatar:', error);
          reject(error);
        }
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  };
*/
  // Функції збереження для кожної секції
  const saveGeneral = async (extraData: Record<string, any> = {}) => {
    try {
      const dataToSave = {
        firstname: profileData.firstname,
        lastname: profileData.lastname,
        ...extraData, // Для avatar (тепер URL замість base64)
      };

      await updateGeneralProfile(dataToSave);
      showSuccess('General information saved successfully!');

      // Оновлюємо стан після успішного збереження
      const newProfileData = { ...profileData, ...extraData };
      setProfileData(newProfileData);
      setInitialData(newProfileData);
    } catch (error) {
      showError('Failed to save general information.');
      console.error('Failed to save general information', error);
      throw error;
    }
  };

  const savePersonalDetails = async () => {
    try {
      const personalFields = {
        personal_country: profileData.personal_country,
        personal_province: profileData.personal_province,
        personal_city: profileData.personal_city,
        personal_address: profileData.personal_address,
        personal_apt_suite: profileData.personal_apt_suite,
        personal_unit: profileData.personal_unit,
        personal_municipality: profileData.personal_municipality,
        personal_postal_code: profileData.personal_postal_code,
        personal_phone: profileData.personal_phone,
        personal_info_agreement: profileData.personal_info_agreement,
        terms_privacy_agreement: profileData.terms_privacy_agreement,
        personal_age_group: profileData.personal_age_group,
      };

      await updatePersonalProfile({ personal: personalFields });
      showSuccess('Personal information saved successfully!');

      setInitialData({ ...profileData });
    } catch (error) {
      showError('Failed to save personal information.');
      console.error('Failed to save personal information', error);
      throw error;
    }
  };

  const saveBilling = async () => {
    try {
      const billingFields: any = {
        billing_authorization: profileData.billing_authorization,
      };

      if (isTenant) {
        // Поля для тенанта
        billingFields.tenant_pad_account_holder =
          profileData.tenant_pad_account_holder;
        billingFields.tenant_pad_bank_name = profileData.tenant_pad_bank_name;
        billingFields.tenant_pad_bank_branch_address =
          profileData.tenant_pad_bank_branch_address;
        billingFields.tenant_pad_transit_number =
          profileData.tenant_pad_transit_number;
        billingFields.tenant_pad_institution_number =
          profileData.tenant_pad_institution_number;
        billingFields.tenant_pad_account_number =
          profileData.tenant_pad_account_number;
        billingFields.tenant_pad_account_type =
          profileData.tenant_pad_account_type;
        billingFields.tenant_pad_swift_bic_code =
          profileData.tenant_pad_swift_bic_code;
        billingFields.tenant_pad_authorization =
          profileData.tenant_pad_authorization;
      } else {
        // Поля для лендлорда
        billingFields.account_holder_first_name =
          profileData.account_holder_first_name;
        billingFields.account_holder_last_name =
          profileData.account_holder_last_name;
        billingFields.bank_name = profileData.bank_name;
        billingFields.bank_branch_address = profileData.bank_branch_address;
        billingFields.transit_number = profileData.transit_number;
        billingFields.institution_number = profileData.institution_number;
        billingFields.account_number = profileData.account_number;
        billingFields.account_type = profileData.account_type;
        billingFields.swift_bic_code = profileData.swift_bic_code;
      }

      await updateBillingProfile({ billing: billingFields });
      showSuccess('Banking information saved successfully!');

      setInitialData({ ...profileData });
    } catch (error) {
      showError('Failed to save banking information.');
      console.error('Failed to save banking information', error);
      throw error;
    }
  };

  const saveCompany = async () => {
    try {
      const companyFields = {
        company_name: profileData.company_name,
        company_position: profileData.company_position,
        company_country: profileData.company_country,
        company_province: profileData.company_province,
        company_city: profileData.company_city,
        company_address: profileData.company_address,
        company_apt_suite: profileData.company_apt_suite,
        company_unit: profileData.company_unit,
        company_municipality: profileData.company_municipality,
        company_postal_code: profileData.company_postal_code,
        company_phone: profileData.company_phone,
        company_email: profileData.company_email,
        company_department: profileData.company_department,
      };

      await updateCompanyProfile({ company: companyFields });
      showSuccess('Company information saved successfully!');

      setInitialData({ ...profileData });
    } catch (error) {
      showError('Failed to save company information.');
      console.error('Failed to save company information', error);
      throw error;
    }
  };

  // Вибір функції збереження залежно від секції
  const handleSave = async (extraData: Record<string, any> = {}) => {
    switch (sectionType) {
      case 'general':
        return await saveGeneral(extraData);
      case 'personal-details':
        return await savePersonalDetails();
      case 'billing':
        return await saveBilling();
      case 'company':
        return await saveCompany();
      default:
        throw new Error(`Unknown section type: ${sectionType}`);
    }
  };

  // Функція очищення (повертає до початкового стану)
  const handleClear = () => {
    setProfileData({ ...initialData });
  };

  // Допоміжні функції для роботи з булевими значеннями
  const getAgreementValue = (fieldKey: keyof ProfileData): boolean => {
    const value = profileData[fieldKey] as string;
    return value === 'true' || value === '1' || value === 'yes';
  };

  const setAgreementValue = (fieldKey: keyof ProfileData, checked: boolean) => {
    handleChange(fieldKey, checked ? 'true' : 'false');
  };

  // Валідація залежно від секції
  const isFormValid = useCallback(() => {
    switch (sectionType) {
      case 'general':
        return true; // Загальна секція завжди валідна

      case 'personal-details':
        return (
          getAgreementValue('personal_info_agreement') &&
          getAgreementValue('terms_privacy_agreement')
        );

      case 'billing':
        if (isTenant) {
          return (
            getAgreementValue('billing_authorization') &&
            getAgreementValue('tenant_pad_authorization')
          );
        } else {
          return getAgreementValue('billing_authorization');
        }

      case 'company':
        return true; // Компанійна секція завжди валідна

      default:
        return true;
    }
  }, [sectionType, profileData, isTenant]);

  return {
    data: profileData,
    initialData,
    loading,
    handleChange,
    handleSave,
    handleClear,
    getAgreementValue,
    setAgreementValue,
    isFormValid,
   // uploadAvatar, // Додаємо новий метод для завантаження аватарів
  };
};
